import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {_openModal} from '../../../../shared/reducers/modals';
import {AccountNameEdit, AccountNumberMask, Checkbox, Dropdown, Icon, LoadingSpinner, TaxIDMask} from '../../../../shared';
import Table from '../../../../shared/components/Table';
import debug from 'debug';
import {dataFeedStatuses, gatherData, getRiskLabel, getUpdatedFilter} from '../../../../shared/utils/utils';
import useQueryObject from '../../../../shared/utils/useQueryObject';
import {getRiskPartner, isEntitled} from '../../../../shared/reducers/user';
import {withSize} from 'react-sizeme';
import {_getConfig} from '../../../../shared/reducers/config';
import useSWR from 'swr';
import useQueryString from '../../../../shared/utils/useQueryString';
import {openRiskPartner} from '../../../utils/partners';
import {getInstitutions} from '../../../../shared/utils/containerUtils';
import RiskBox from '../../../../shared/components/analyze/RiskBox';
import {managedAccountValueRender} from '../../../sections/manage/accounts/Managed';
import {withTheme} from 'styled-components/macro';
import {formatAssignedModels} from '../../../../shared/utils/models';

const log = debug('circleblack:advisor:analyze:accounts:table');

const AccountsTable = ({
  container,
  openModal,
  size,
  config,
  getConfig,
  onRowClick,
  rowsPerPage,
  maxHeight,
  height = '200px',
  rowOnClickDisabled,
  fullPage,
  theme,
}) => {
  const {data: con} = useSWR(container && `/containers/${container.id}`, null, {fallbackData: container});

  const riskProfiles = [
    {name: 'Conservative', color: theme.dark ? '#99FA66' : '#79AF30'},
    {name: 'Growth & Income', color: '#9F3D93'},
    {name: 'Aggressive', color: theme.dark ? '#FFCC03' : '#CF790B'},
    {name: 'Unknown', color: '#797B83'},
  ];

  const [institutionName] = useQueryString('institutionName');
  const [balancingAlert] = useQueryString('balancingAlert');
  const [riskProfile] = useQueryString('riskProfile');
  const [isRetirement] = useQueryString('isRetirement');
  const [typeName] = useQueryString('typeName');

  const retirementOptions = [
    {label: 'Retirement', value: '1'},
    {label: 'Non-Retirement', value: '0'},
  ];
  const [filters, setFilters] = useQueryObject(
    'accountTableFilters',
    {
      institutionName,
      balancingAlert: balancingAlert && {label: balancingAlert, value: balancingAlert} || undefined,
      riskProfile: riskProfile && {label: riskProfile, value: riskProfile} || undefined,
      isRetirement: isRetirement && retirementOptions.find(r => r.value === isRetirement) || undefined,
      typeName: typeName && {label: typeName, value: typeName} || undefined,
    }
  );
  const gatherAccounts = con && gatherData(`/containers/${con.id}/accounts`);

  const dataManager = {
    gatherData: inputValue => gatherAccounts({...inputValue, ...getUpdatedFilter(filters)}),
  };

  React.useEffect(() => {
    if (dataManager.refresh) {
      dataManager.refresh();
    }
  }, [con, filters]);

  const {data: accountTypes} = useSWR(con && con.id && `/containers/${con.id}/accounts/types`);

  const {data: institutions} = getInstitutions({
    con,
    options: {
      onError: err => openModal({type: 'Error', title: 'Account Institutions', err}),
      onSuccess: input => {
        if (institutionName) {
          setFilters(current => ({...current, institutionName: input.find(i => i.label === institutionName)}));
        }
      },
    },
  });

  const advisorsButton = account => <Icon svg={'advisorsCircle'} action={() => openModal({
    type: 'AccountAdvisors',
    account,
    onSuccess: dataManager.refresh,
  })}/>;

  React.useEffect(() => {
    getConfig();
  }, []);

  const driftStatuses = React.useMemo(() => {
    if (!(config.models && config.models.driftStatuses)) {
      return [];
    }
    return Object.values(config.models.driftStatuses)
      .filter(s => s.status !== 'Not Applicable')
      .map(status => ({
        title: status.status,
        image: status.icon,
        description: status.description,
        color: status.color,
        category: 'Drift',
        displayCondition: a => a.balancingAlert === status.status,
      }));
  }, [config]);

  const updateFilters = (name, value) => setFilters({...filters, [name]: value});

  const riskPartner = getRiskPartner();

  const clientTaxIDRender = account => (
    <TaxIDMask taxID={account.clientTaxID}/>
  );

  const accountNameRender = instance => <AccountNameEdit size={size}
                                                         account={instance}
                                                         openModal={openModal}
                                                         dataManager={dataManager}/>;

  const riskNumber = account => <RiskBox
    margin={'0'}
    action={() => openRiskPartner({
      accountID: account.id,
      totumID: account.riskNumber !== null && account.clientID,
    })}
    riskPartner={riskPartner}
    riskNumber={account.riskNumber} size={'sm'}
    type='standard'
  />;

  log('rendering');

  if (!con) {
    return <LoadingSpinner/>;
  }

  return <Table dataManager={dataManager}
                columns={[
                  {
                    title: 'Account Name',
                    dataPoint: 'name',
                    type: 'text',
                    minWidth: 150,
                    isSortable: true,
                    render: accountNameRender,
                    // render: (row, instance)=> <AccountNameEdit size={size} instance={instance} row={row} openModal={openModal} dataManager={dataManager}/>;
                  },
                  {
                    title: 'Account #',
                    dataPoint: 'number',
                    type: 'custom',
                    alignment: 'left',
                    render: AccountNumberMask,
                    minWidth: 80,
                    isSortable: true,
                  },
                  (fullPage || size.width > 850) && isEntitled([{group: 'advisor', feature: 'accountCode', access: 'r'}]) && {
                    title: 'Account Code',
                    dataPoint: 'code',
                    type: 'text',
                    minWidth: 60,
                    isSortable: true,
                  },
                  (fullPage || size.width > 400) && {
                    title: 'Type',
                    dataPoint: 'typeName',
                    type: 'text',
                    minWidth: 80,
                    isSortable: true,
                  },
                  (fullPage || size.width > 500)
                  && isEntitled([{group: 'account', feature: 'aggrManAccInstName', access: 'r'}])
                  && {
                    title: 'Institution',
                    dataPoint: 'institution.name',
                    type: 'text',
                    minWidth: 100,
                    isSortable: true,
                  },
                  (fullPage || size.width > 500)
                  && !isEntitled([{group: 'account', feature: 'aggrManAccInstName', access: 'r'}])
                  && {
                    title: 'Custody',
                    dataPoint: 'custody',
                    type: 'text',
                    minWidth: 100,
                    isSortable: true,
                  },
                  (fullPage || size.width > 660) && {
                    title: 'Client',
                    dataPoint: 'clientDisplayNameOrdered',
                    type: 'text',
                    minWidth: 80,
                    isSortable: true,
                  },
                  (fullPage || size.width > 750) && {
                    title: 'Tax ID',
                    dataPoint: 'clientTaxID',
                    type: 'custom',
                    alignment: 'left',
                    render: clientTaxIDRender,
                    minWidth: 90,
                    isSortable: true,
                  },
                  (fullPage || size.width > 660) && {
                    title: 'Household',
                    dataPoint: 'householdName',
                    type: 'text',
                    minWidth: 80,
                    isSortable: true,
                  },
                  size.width > 1174 && {
                    title: 'Assigned Model',
                    dataPoint: 'models',
                    type: 'custom',
                    alignment: 'left',
                    render: ticket => formatAssignedModels(ticket.models, {asHTML: true, justifyHTML: 'left', emptyText: '-'}),
                    minWidth: 175,
                  },
                  {
                    title: 'Assets',
                    dataPoint: 'balance',
                    type: 'custom',
                    alignment: 'right',
                    render: managedAccountValueRender,
                    minWidth: 80,
                    isSortable: true,
                  },
                  (fullPage || size.width > 850) && ['FIRM', 'BRANCH'].includes(con.type) && {
                    title: 'Advisors',
                    dataPoint: 'advisors',
                    type: 'custom',
                    render: advisorsButton,
                    minWidth: 50,
                    isSortable: false,
                  },
                  (fullPage || size.width > 850) && riskPartner && {
                    title: getRiskLabel(riskPartner),
                    dataPoint: 'riskNumber',
                    type: 'custom',
                    render: riskNumber,
                    minWidth: 50,
                    isSortable: false,
                  },
                ].filter(c => !!c)}
                defaultSort={{
                  column: 'balance',
                  desc: true,
                }}
                searches={[
                  {
                    title: 'Account Name',
                    name: 'displayName',
                    isFuzzy: true,
                  },
                  {
                    title: 'Account #',
                    name: 'number',
                    isFuzzy: true,
                  },
                  {
                    title: 'Client',
                    name: 'clientDisplayNameOrdered',
                    isFuzzy: true,
                  },
                  {
                    title: 'Household',
                    name: 'householdName',
                    isFuzzy: true,
                  },
                  isEntitled([{group: 'advisor', feature: 'accountCode', access: 'r'}]) && {
                    title: 'Account Code',
                    name: 'code',
                    isFuzzy: true,
                  },
                ].filter(s => !!s)}
                rowIdentifier={'id'}
                rowOnClick={onRowClick}
                rowOnClickDisabled={rowOnClickDisabled}
                iconsKey={[
                  {
                    image: 'dotNew',
                    title: 'Is New',
                    color: '#00D0BE',
                    description: 'Indicates New Account',
                    displayCondition: a => !!a.isNew,
                    category: 'General',
                  },
                  ...driftStatuses,
                  ...Object.values(dataFeedStatuses) // Correct code but get /crm/accounts doesn't calculate data feed status
                    .map(d => ({
                      ...d,
                      title: d.status,
                      image: d.icon,
                      category: 'Data Feeds',
                      displayCondition: a => a.dataFeedStatus === d.status,
                    })),
                ]}
                filters={[
                  <Checkbox
                    key={'isNew'}
                    title={'New'}
                    dataPoint={{
                      name: 'isNew',
                      value: !!filters.isNew,
                    }}
                    onChange={(name, value) => updateFilters(name, value || undefined)}
                    disabled={false}/>,
                  accountTypes && <Dropdown title={'Account Type'}
                                            dataPoint={{name: 'typeName', value: filters.typeName}}
                                            onChange={updateFilters}
                                            isSearchable={true}
                                            isClearable={true}
                                            options={accountTypes.map(t => ({label: t.typeName, value: t.typeName}))}/>,
                  isEntitled([{group: 'account', feature: 'institution', access: 'r'}])
                  && institutions
                  && <Dropdown title={isEntitled([{group: 'account', feature: 'aggrManAccInstName', access: 'r'}])
                    ? 'Institution'
                    : 'Custody'}
                               onChange={updateFilters}
                               isSearchable={true}
                               isClearable={true}
                               options={institutions}
                               dataPoint={{
                                 name: 'institutionName',
                                 value: filters.institutionName,
                               }}/>,
                  <Dropdown key={'dataFeedStatusSelect'}
                            title={'Data Feed Status'}
                            onChange={updateFilters}
                            isSearchable={true}
                            isClearable={true}
                            options={Object.values(dataFeedStatuses).map(i => ({
                              label: i.status,
                              value: i.status,
                            }))}
                            dataPoint={{
                              name: 'dataFeedStatus',
                              value: filters.dataFeedStatus,
                            }}/>,
                  isEntitled([{group: 'models', feature: 'drift', access: 'r'}])
                  && <Dropdown title={'Drift Status'}
                               onChange={updateFilters}
                               isSearchable={true}
                               isClearable={true}
                               options={driftStatuses.map(i => ({
                                 label: i.title,
                                 value: i.title,
                               }))}
                               dataPoint={{
                                 name: 'balancingAlert',
                                 value: filters.balancingAlert,
                               }}/>,
                  <Dropdown title={'Risk Profile'}
                            key={'Risk Profile Filter'}
                            onChange={updateFilters}
                            isSearchable={true}
                            isClearable={true}
                            options={riskProfiles.map(i => ({
                              label: i.name,
                              value: i.name,
                            }))}
                            dataPoint={{
                              name: 'riskProfile',
                              value: filters.riskProfile,
                            }}/>,
                  <Dropdown title={'Retirement'}
                            key={'Retirement Filter'}
                            onChange={updateFilters}
                            isSearchable={true}
                            isClearable={true}
                            options={retirementOptions}
                            dataPoint={{
                              name: 'isRetirement',
                              value: filters.isRetirement,
                            }}/>,
                ].filter(f => !!f)}
                rowsPerPage={rowsPerPage}
                filterValues={filters}
                maxHeight={maxHeight}
                height={height}/>;
};

const mapStateToProps = state => ({
  config: state.config,
});

const mapDispatchToProps = (dispatch) => ({
  getConfig: force => dispatch(_getConfig(force)),
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default withTheme(compose(withSize(), connect(mapStateToProps, mapDispatchToProps))(AccountsTable));

AccountsTable.propTypes = {
  /**
   * SizeMe size
   */
  size: PropTypes.shape({
    width: PropTypes.number,
  }),
  openModal: PropTypes.func.isRequired,
  container: PropTypes.shape({id: PropTypes.number.isRequired, type: PropTypes.string.isRequired}).isRequired,
  config: PropTypes.shape({models: PropTypes.shape({driftStatuses: PropTypes.object})}).isRequired,
  getConfig: PropTypes.func.isRequired,
  /**
   * Optional on row click action
   */
  onRowClick: PropTypes.func,
  /**
   * Optional function to disable on row click
   */
  rowOnClickDisabled: PropTypes.func,
  /**
   * Optional rows per page override
   */
  rowsPerPage: PropTypes.number,
  /**
   * Optional max height to pass to table
   */
  maxHeight: PropTypes.string,
  /**
   * Optional max height to pass to table, defaults to 200px
   */
  height: PropTypes.string,
  /**
   * Indicates this is a full page and should not have size restrictions
   */
  fullPage: PropTypes.bool,
  theme: PropTypes.object,
};
